import { Download, Icon, QrCode as QrCodeIcon, Share2, UserPlus } from "@norican/icons";
import { Button, Typography } from "@norican/react";
import { useEffect, useState } from "react";

import { QrDialog } from "@/features/card/qr-dialog";
import type { CardProfile } from "@/lib/api";
import {
  cardImageFile,
  downloadBlob,
  qrPngBlob,
  qrSvgBlob,
  safeFilename,
  shareCard,
  vcardFile,
} from "@/lib/downloads";
import { logger } from "@/lib/logger";

interface CardActionsProps {
  card: CardProfile;
}

const CAN_SHARE = typeof navigator !== "undefined" && typeof navigator.share === "function";

/** Everything someone does with a card: show it for scanning, save it, pass it on. */
export function CardActions({ card }: CardActionsProps) {
  const [qrOpen, setQrOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  // The image Share sends, rendered ahead of the tap (see `shareCard`); kept
  // with the vCard it was drawn from, so a card that changed is never shared.
  const [shareImage, setShareImage] = useState<{ vcard: string; file: File } | null>(null);

  useEffect(() => {
    if (!CAN_SHARE) {
      return;
    }
    let current = true;
    cardImageFile(card)
      .then((file) => {
        if (current) {
          setShareImage({ vcard: card.vcard, file });
        }
      })
      .catch((err: unknown) =>
        logger.warn("[card] card image failed, Share renders it on tap:", err),
      );
    return () => {
      current = false;
    };
  }, [card]);

  const run = async (label: string, action: () => Promise<string | void> | string | void) => {
    setBusy(true);
    setStatus("");
    try {
      const message = await action();
      if (message) {
        setStatus(message);
      }
    } catch (err) {
      logger.error(`[card] ${label} failed:`, err);
      setStatus(`${label} failed. Please try again.`);
    } finally {
      setBusy(false);
    }
  };

  const saveContact = () =>
    run("Save contact", () => {
      const file = vcardFile(card.vcard, card.display_name);
      downloadBlob(file, file.name);
      return `${file.name} downloaded.`;
    });

  const share = () =>
    run("Share", async () => {
      const image = shareImage?.vcard === card.vcard ? shareImage.file : null;
      const outcome = await shareCard(card, image);
      return outcome === "downloaded"
        ? "Sharing is not available here, the card image was downloaded instead."
        : "";
    });

  const downloadPng = () =>
    run("QR export", async () => {
      downloadBlob(await qrPngBlob(card.vcard), `${safeFilename(card.display_name)}-qr.png`);
      return "QR code downloaded as PNG.";
    });

  const downloadSvg = () =>
    run("QR export", () => {
      downloadBlob(qrSvgBlob(card.vcard), `${safeFilename(card.display_name)}-qr.svg`);
      return "QR code downloaded as SVG.";
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Button
          tone="accent"
          onClick={() => setQrOpen(true)}
          startIcon={<Icon icon={QrCodeIcon} size={16} />}
        >
          Show QR code
        </Button>
        <Button
          variant="outline"
          tone="neutral"
          disabled={busy}
          onClick={saveContact}
          startIcon={<Icon icon={UserPlus} size={16} />}
        >
          Save contact
        </Button>
        {CAN_SHARE && (
          <Button
            variant="outline"
            tone="neutral"
            disabled={busy}
            onClick={share}
            startIcon={<Icon icon={Share2} size={16} />}
          >
            Share
          </Button>
        )}
        <Button
          variant="ghost"
          tone="neutral"
          disabled={busy}
          onClick={downloadPng}
          startIcon={<Icon icon={Download} size={16} />}
        >
          QR as PNG
        </Button>
        <Button
          variant="ghost"
          tone="neutral"
          disabled={busy}
          onClick={downloadSvg}
          startIcon={<Icon icon={Download} size={16} />}
        >
          QR as SVG
        </Button>
      </div>
      {status && (
        <Typography variant="body-sm" tone="muted" role="status">
          {status}
        </Typography>
      )}
      <QrDialog card={card} open={qrOpen} onOpenChange={setQrOpen} />
    </div>
  );
}
